import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, PanelLeftClose, PanelLeft } from 'lucide-react';
import { CloudSidebar } from './CloudSidebar.js';
import { useAuth } from '../../contexts/auth.js';
import { PaymentGate, TrialBanner } from '../billing/PaymentGate.js';

interface CloudShellProps {
  children: React.ReactNode;
}

export function CloudShell({ children }: CloudShellProps) {
  const location = useLocation();
  const { claims } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('r42-sidebar-collapsed') === '1'; } catch { return false; }
  });

  // Persist collapsed state
  useEffect(() => {
    try { localStorage.setItem('r42-sidebar-collapsed', sidebarCollapsed ? '1' : '0'); } catch { /* noop */ }
  }, [sidebarCollapsed]);

  // Close sidebar on navigation
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (!isSidebarOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isSidebarOpen]);

  // Close on Escape / resize past lg
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsSidebarOpen(false);
    };
    const handleResize = () => {
      if (window.innerWidth >= 1024) setIsSidebarOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const userInitial = (claims?.name?.[0] ?? claims?.email?.[0] ?? 'R').toUpperCase();

  const [paymentGateDismissed, setPaymentGateDismissed] = useState(() => {
    try { return sessionStorage.getItem('r42-payment-gate-dismissed') === '1'; } catch { return false; }
  });
  function handlePaymentGateDismiss() {
    setPaymentGateDismissed(true);
    try { sessionStorage.setItem('r42-payment-gate-dismissed', '1'); } catch { /* noop */ }
  }

  return (
    <div className="min-h-dvh bg-[var(--color-bg-dark)] text-[var(--color-text-primary)] lg:flex">
      {/* Payment gate — shown on login if no payment method */}
      {!paymentGateDismissed && <PaymentGate onDismiss={handlePaymentGateDismiss} />}
      {/* Trial banner — persistent top-right badge */}
      <TrialBanner />
      {/* Desktop sidebar — retractable */}
      <div className={`hidden lg:block lg:shrink-0 transition-all duration-300 ease-in-out overflow-hidden ${sidebarCollapsed ? 'w-0' : 'w-[16rem]'}`}>
        <CloudSidebar />
      </div>

      {/* Main area */}
      <div className="flex min-h-dvh min-w-0 flex-1 flex-col lg:max-h-dvh">
        {/* Mobile header */}
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-[var(--color-border-default)] bg-[var(--color-bg-dark)]/95 px-4 py-3 backdrop-blur-lg lg:hidden">
          <button
            type="button"
            onClick={() => setIsSidebarOpen(true)}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[var(--color-border-default)] bg-[var(--color-surface-glass)] text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
            aria-label="Open navigation menu"
            aria-expanded={isSidebarOpen}
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/45">
              Rule42
            </p>
            <p className="truncate text-sm font-semibold text-white">Cloud</p>
          </div>
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[var(--color-border-default)] bg-[var(--color-surface-glass)] text-sm font-semibold text-[var(--color-text-secondary)]">
            {userInitial}
          </div>
        </header>

        {/* Page content */}
        <main className="relative min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
          {/* Desktop sidebar toggle */}
          <button
            type="button"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="fixed left-2 top-3 z-40 hidden lg:flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-dark)]/90 text-[var(--color-text-muted)] backdrop-blur-sm transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-secondary)]"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
          {children}
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {isSidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/75 dark:bg-slate-950/75 light:bg-slate-900/75"
            onClick={() => setIsSidebarOpen(false)}
            aria-label="Close navigation menu"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            className="absolute inset-y-0 left-0 w-[min(18rem,calc(100vw-1.25rem))]"
          >
            <CloudSidebar mobile onNavigate={() => setIsSidebarOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
